// FindTherapists.tsx - UPDATED
import React, { useState, useEffect, useRef, useCallback } from 'react'
import api from '@/services/api'
import { usePatientSearchStore } from '@/store/usePatientSearchStore'
import TherapistBooking from '../TherapistBooking'
import {
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Fade,
    Snackbar,
    useMediaQuery,
    useTheme,
    Box,
    Typography,
    Paper,
    Stack,
    Grid,
    Chip,
    Button,
    IconButton,
    alpha,
    Avatar,
} from '@mui/material'
import {
    useNavigate,
    useLocation,
    Link,
    useSearchParams,
} from 'react-router-dom'
import RoomIcon from '@mui/icons-material/Room'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import InfoIcon from '@mui/icons-material/Info'
import CloseIcon from '@mui/icons-material/Close'
import LanguageIcon from '@mui/icons-material/Language'
import ScheduleIcon from '@mui/icons-material/Schedule'
import PaymentIcon from '@mui/icons-material/Payment'
import DescriptionIcon from '@mui/icons-material/Description'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { formatTherapistData } from '../Utils'
import { UserRole } from '@/types'
import { useAppSelector } from '@/store'
import { ArrowBack, Person } from '@mui/icons-material'
import AdvancedPagination from '@components/pagination/AdvancedPagination'
import SignupHeader from '../PatientPages/SignupHeader'

// Add these interfaces for the map
interface MapTherapist extends Therapist {
    marker?: google.maps.Marker
}

// Updated interfaces
interface Package {
    id: string
    name: string
    rate: string
    duration: string
    description: string
    type: string
}

interface Service {
    _id: string
    name: string
    specialization?: {
        _id: string
        name: string
        description?: string
    }
    short_description?: string
    long_description?: string
    min_age?: number
    max_age?: number
    language?: string[]
    sessionFee?: number
    session_length?: number
    session_buffer?: number
    travelOnsiteSurcharge?: number
    cancellationWindowHours?: number
    noShowFee?: number
    lateCancellationFee?: number
    service_radius?: number
    specility?: any[]
    tags?: string[]
    status?: string
    createdAt?: string
    updatedAt?: string
    insurance?: Array<{
        _id: string
        payer_name: string
        [key: string]: any
    }>
}

interface Therapist {
    firstName: string
    id: string
    name: string
    title: string
    experience: string
    image: string
    specialties: string[]
    insurances: string[]
    rate: string
    packages: Package[]
    services?: Service[] // Store original services data
    city?: string
    state?: string
    location?: string
    distance?: string
    rating?: number
    reviews?: number
    latitude?: number
    longitude?: number
    specialization?: any[]
    insuranceAccepted?: any[]
    selectedPackage: Package | null
    miles?: string
}

interface SearchParams {
    insurance?: string
    specialty?: string
    radius?: string
    location?: string
    lat?: number | string | null
    lng?: number | string | null
    insuranceId?: string
    specialtyId?: string
    page?: number | string
    limit?: number | string
}

interface ListComponentProps {
    initialSearchParams?: SearchParams
    hasSearched?: boolean
    type?: string
}

const ListComponent: React.FC<ListComponentProps> = ({
    initialSearchParams = {},
    hasSearched = false,
    type
}) => {
    const [selectedPackages, setSelectedPackages] = useState<
        Record<string, string | null>
    >({})
    const [selectedPackage, setSelectedPackage] = useState<Package | null>(null)
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'error' as 'error' | 'success' | 'info' | 'warning',
    })

    // Map references
    const mapRef = useRef<HTMLDivElement>(null)
    const googleMapRef = useRef<google.maps.Map | null>(null)
    const markersRef = useRef<google.maps.Marker[]>([])
    const infoWindowsRef = useRef<google.maps.InfoWindow[]>([])
    const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null)

    // ✅ Use patient search store
    const {
        location,
        radius,
        insuranceId,
        insuranceName,
        specialtyId,
        specialtyName,
        setLocation,
        setRadius,
        setInsurance,
        setSpecialty,
        clearSearch,
        // searchPatients, // Add the search function from store
    } = usePatientSearchStore()

    const [therapists, setTherapists] = useState<Therapist[]>([])
    const [loading, setLoading] = useState(false)
    const [totalTherapists, setTotalTherapists] = useState(0)
    const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({
        lat: 34.0522,
        lng: -118.2437,
    })
    const [showContractModal, setShowContractModal] = useState(false)
    const [selectedTherapist, setSelectedTherapist] = useState<Therapist | null>(
        null
    )
    const [mapLoaded, setMapLoaded] = useState(false)
    const [isInitialLoad, setIsInitialLoad] = useState(true)
    const hasFetchedRef = useRef(false)
    const [packageDetailsDialogOpen, setPackageDetailsDialogOpen] =
        useState(false)
    const [selectedServiceForDetails, setSelectedServiceForDetails] =
        useState<Service | null>(null)
    const [loginDialogOpen, setLoginDialogOpen] = useState(false)
    const [therapistForBooking, setTherapistForBooking] =
        useState<Therapist | null>(null)
    const navigate = useNavigate()
    const routerLocation = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const { user, isAuthenticated } = useAppSelector(state => state.auth)
    const isPatientAuthenticated =
        isAuthenticated && user?.role === UserRole.CLIENT

    // Check if user came from landing page
    const fromLandingPage = routerLocation.state?.fromLandingPage === true

    // Get search params from navigation state (from Navbar search)
    const navSearchParams = routerLocation.state?.searchParams as
        | SearchParams
        | undefined
    const navHasSearched = routerLocation.state?.hasSearched === true

    // Pagination state - initialized from URL query params
    const [page, setPage] = useState<number>(() => {
        const pageParam = searchParams.get('page')
        return pageParam ? Math.max(0, parseInt(pageParam, 10) - 1) : 0
    })
    const [patientId, setPatientId] = useState("")
    const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
        const limitParam = searchParams.get('limit')
        return limitParam ? parseInt(limitParam, 10) : 10
    })

    const getPatientId = async () => {
        try {
            const res = await api.get(`/patient/profile`)
            setPatientId(res?.data?.data?._id || '')
        } catch (error) {
            console.log('Error fetching patient profile:', error)
            setPatientId('')
        }
    }

    useEffect(() => {
        if (isAuthenticated) {
            getPatientId()
        } else {
            setPatientId('')
        }
    }, [isAuthenticated])

    // ✅ Cleaner helper function to get location data
    const getLocationData = () => {
        return {
            address: location?.address || '',
            lat: location?.lat ?? null,
            lng: location?.lng ?? null,
        }
    }

    // ✅ Fetch therapists API function
    const fetchTherapists = async (searchParamsToUse: SearchParams) => {
        setLoading(true)

        try {
            const params: any = {
                type: type,
                page: String(searchParamsToUse.page || page + 1),
                limit: String(searchParamsToUse.limit || rowsPerPage),
            }

            // PRIORITIZE IDs OVER NAMES FOR API CALL (like FindTherapists)
            if (searchParamsToUse.insuranceId && searchParamsToUse.insuranceId !== 'null') {
                params.insurance = searchParamsToUse.insuranceId
            } else if (searchParamsToUse.insurance && searchParamsToUse.insurance !== 'null') {
                params.insurance = searchParamsToUse.insurance
            }

            if (searchParamsToUse.specialtyId && searchParamsToUse.specialtyId !== 'null') {
                params.specialty = searchParamsToUse.specialtyId
            } else if (searchParamsToUse.specialty && searchParamsToUse.specialty !== 'null') {
                params.specialty = searchParamsToUse.specialty
            }

            if (searchParamsToUse.radius && searchParamsToUse.radius !== 'null') {
                params.radius = searchParamsToUse.radius
            }
            if (searchParamsToUse.location && searchParamsToUse.location !== 'null') {
                params.location = searchParamsToUse.location
            }

            const locationData = getLocationData()
            let lat = searchParamsToUse.lat ?? locationData.lat
            let lng = searchParamsToUse.lng ?? locationData.lng

            // If lat/lng still not provided, try to get current position for distance calculations (GUESTS ONLY)
            if ((lat == null || lat === 'null') && (lng == null || lng === 'null') && !isPatientAuthenticated) {
                try {
                    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                        navigator.geolocation.getCurrentPosition(resolve, reject, {
                            timeout: 5000,
                            enableHighAccuracy: false,
                            maximumAge: 30000
                        })
                    })
                    lat = position.coords.latitude
                    lng = position.coords.longitude
                } catch (error) {
                    // console.warn('Geolocation failed or denied', error)
                }
            }

            if (lat != null && lat !== 'null') {
                params.lat = String(lat)
            }
            if (lng != null && lng !== 'null') {
                params.long = String(lng)
            }

            // Prioritize the patientId from state or auth user
            const effectivePatientId = patientId || (isPatientAuthenticated ? user?.id : '')
            if (effectivePatientId) {
                params.patientId = effectivePatientId
            }

            // Remove empty/null/undefined params
            Object.keys(params).forEach(key => {
                if (
                    params[key] === null ||
                    params[key] === undefined ||
                    params[key] === ''
                ) {
                    delete params[key]
                }
            })

            const response = await api.get('/contracts/search-providers-manual', {
                params,
            })



            const responseData = response.data?.data || response.data
            const therapistsData = responseData?.results || []
            const totalCount = responseData?.total || 0

            const transformed: Therapist[] = therapistsData?.map((item: any) => {
                const therapist = item?.therapist_id
                const services = item?.services || []

                // Map packages from services
                const packages: Package[] = services.map((service: any) => ({
                    id: service?._id,
                    name: service?.name,
                    rate: `$${service?.sessionFee || 0}`,
                    duration: `${service?.session_length || 0} mins`,
                    description: service?.short_description || service?.name,
                    type: 'In-Person',
                }))

                // Correctly map specialties
                const specialties =
                    therapist?.specialization?.map(
                        (spec: any) => spec?.name || `Specialty ${spec}`
                    ) || ['General Therapy']

                // Correctly map insurances
                const insurances =
                    therapist?.insuranceAccepted?.map(
                        (insurance: any) =>
                            insurance?.payer_name || `Insurance ${insurance}`
                    ) || ['Private Insurance']

                // Calculate rate range
                let rateRange = '$100 - $200/session'
                if (services.length > 0) {
                    const fees = services
                        .map((s: any) => s.sessionFee || 0)
                        .filter((fee: number) => fee > 0)

                    if (fees.length > 0) {
                        const minFee = Math.min(...fees)
                        const maxFee = Math.max(...fees)
                        rateRange = `$${minFee} - $${maxFee}/session`
                    }
                }

                const therapistLocation =
                    therapist?.city && therapist?.state
                        ? `${therapist?.city}, ${therapist?.state}`
                        : 'Location not specified'

                // Get coordinates
                const latitude =
                    therapist?.latitude || therapist?.location?.coordinates?.[1] || null
                const longitude =
                    therapist?.longitude || therapist?.location?.coordinates?.[0] || null

                return {
                    id: therapist?._id || '',
                    firstName: therapist?.firstName || '',
                    name: `${therapist?.firstName || ''} ${therapist?.lastName || ''
                        }`.trim(),
                    title: getTitleFromDegrees(therapist?.degrees),
                    experience: therapist?.yearsOfExperience
                        ? `${therapist?.yearsOfExperience} years of experience`
                        : 'Experienced Provider',
                    specialties: specialties,
                    insurances: insurances,
                    rate: rateRange,
                    image: therapist?.profilePhotoUrl || '',
                    packages:
                        packages.length > 0 ? packages : generateFallbackPackages(),
                    services: services,
                    location: therapistLocation,
                    city: therapist?.city,
                    state: therapist?.state,
                    rating: 4.5,
                    reviews: Math.floor(Math.random() * 50) + 10,
                    latitude: latitude,
                    longitude: longitude,
                    specialization: therapist?.specialization || [],
                    insuranceAccepted: therapist?.insuranceAccepted || [],
                    selectedPackage: null,
                    miles: therapist?.miles
                }
            })

            setTherapists(transformed)
            setTotalTherapists(totalCount)
            setIsInitialLoad(false)

            // Update map center
            if (
                transformed.length > 0 &&
                transformed[0].latitude &&
                transformed[0].longitude
            ) {
                setMapCenter({
                    lat: transformed[0].latitude,
                    lng: transformed[0].longitude,
                })
            } else if (lat && lng) {
                setMapCenter({
                    lat: Number(lat),
                    lng: Number(lng),
                })
            }
        } catch (error: any) {
            console.error('Error fetching providers:', error)
            setTherapists([])
            setTotalTherapists(0)
        } finally {
            setLoading(false)
        }
    }

    // ✅ Handle search from SearchBar
    const handleSearch = useCallback(
        (params: SearchParams) => {
            // console.log('FindTherapists: handleSearch called with:', params)

            // Mark that we've fetched (so store changes don't trigger duplicate calls)
            hasFetchedRef.current = true

            // Reset to first page when performing a new search
            setPage(0)
            const newParams = new URLSearchParams(searchParams)
            newParams.set('page', '1')
            setSearchParams(newParams, { replace: true })

            // Update the patient store with new search params
            if (params.location !== undefined) {
                setLocation({
                    address: params.location || '',
                    lat:
                        params.lat !== undefined && params.lat !== null
                            ? Number(params.lat)
                            : null,
                    lng:
                        params.lng !== undefined && params.lng !== null
                            ? Number(params.lng)
                            : null,
                })
            }

            if (params.radius !== undefined) setRadius(params.radius || null)

            // ✅ Use both id and name for insurance and specialty
            if (params.insuranceId || params.insurance) {
                setInsurance(params.insuranceId || null, params.insurance || null)
            }

            if (params.specialtyId || params.specialty) {
                setSpecialty(params.specialtyId || null, params.specialty || null)
            }

            // Trigger API call directly (don't wait for store updates)
            fetchTherapists({
                ...params,
                lat: params.lat ?? null,
                lng: params.lng ?? null,
                page: 1, // Reset to first page
                limit: rowsPerPage,
            })
        },
        [
            setLocation,
            setRadius,
            setInsurance,
            setSpecialty,
            searchParams,
            setSearchParams,
        ]
    )

    // Pagination handlers
    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        setPage(newPage)
        const newParams = new URLSearchParams(searchParams)
        // TablePagination uses 0-indexed pages, API uses 1-indexed
        newParams.set('page', (newPage + 1).toString())
        setSearchParams(newParams, { replace: true })

        // Call API directly — do NOT rely on the URL effect, which would
        // miss the trigger because setPage() already updated state before
        // the effect could compare old vs new values.
        const locationData = getLocationData()
        fetchTherapists({
            insuranceId: insuranceId || undefined,
            insurance: insuranceName || undefined,
            specialtyId: specialtyId || undefined,
            specialty: specialtyName || undefined,
            radius: radius || undefined,
            location: locationData.address || undefined,
            lat: locationData.lat ?? undefined,
            lng: locationData.lng ?? undefined,
            page: newPage + 1,
            limit: rowsPerPage,
        })
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const newRowsPerPage = parseInt(event.target.value, 10)
        setRowsPerPage(newRowsPerPage)
        setPage(0)
        const newParams = new URLSearchParams(searchParams)
        newParams.set('limit', newRowsPerPage.toString())
        newParams.set('page', '1')
        setSearchParams(newParams, { replace: true })

        // Call API directly for the same reason as handleChangePage
        const locationData = getLocationData()
        fetchTherapists({
            insuranceId: insuranceId || undefined,
            insurance: insuranceName || undefined,
            specialtyId: specialtyId || undefined,
            specialty: specialtyName || undefined,
            radius: radius || undefined,
            location: locationData.address || undefined,
            lat: locationData.lat ?? undefined,
            lng: locationData.lng ?? undefined,
            page: 1,
            limit: newRowsPerPage,
        })
    }



    // ✅ Handle "Search Again" button click
    const handleSearchAgain = () => {

        // If coming from landing page, navigate back to landing page
        if (fromLandingPage) {
            navigate('/')
            return
        }

        // Reset to first page when searching again
        setPage(0)
        const urlParams = new URLSearchParams(searchParams)
        urlParams.set('page', '1')
        setSearchParams(urlParams, { replace: true })

        // Otherwise, perform search again with current params
        const locationData = getLocationData()

        const searchParamsForApi: SearchParams = {
            insuranceId: insuranceId || undefined,
            insurance: insuranceName || undefined,
            specialtyId: specialtyId || undefined,
            specialty: specialtyName || undefined,
            radius: radius || undefined,
            location: locationData.address || undefined,
            lat: locationData.lat ?? undefined,
            lng: locationData.lng ?? undefined,
            page: 1, // Reset to first page
            limit: rowsPerPage,
        }

        // console.log('Search Again params:', searchParamsForApi)
        hasFetchedRef.current = true // Mark as fetched to prevent duplicate calls
        fetchTherapists(searchParamsForApi)
    }

    // ✅ Handle "Go Back" link click
    const handleGoBack = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault()
        // If coming from landing page, navigate back to landing page
        if (fromLandingPage) {
            navigate('/')
        } else {
            // Otherwise, go back in browser history
            navigate(-1)
        }
    }

    // ✅ Effect: Initial render - only fetch if we have initialSearchParams, navSearchParams, or hasSearched flag
    useEffect(() => {
        // Prevent duplicate calls - only fetch once on initial mount
        if (hasFetchedRef.current) return

        // Set default pagination params in URL if not present
        const urlParams = new URLSearchParams(searchParams)
        if (!urlParams.get('page')) {
            urlParams.set('page', '1')
        }
        if (!urlParams.get('limit')) {
            urlParams.set('limit', '10')
        }
        setSearchParams(urlParams, { replace: true })

        // Priority: navSearchParams > initialSearchParams > hasSearched with store values
        // Trigger API even if navSearchParams has all null values (no filters selected)
        if (navSearchParams !== undefined) {
            // Search params from Navbar navigation - update store and trigger API
            // console.log(
            //   'Using navSearchParams from navigation state:',
            //   navSearchParams
            // )
            hasFetchedRef.current = true

            // Update store with search params (only if they have values)
            if (navSearchParams.location !== undefined) {
                setLocation({
                    address: navSearchParams.location || '',
                    lat:
                        navSearchParams.lat !== undefined && navSearchParams.lat !== null
                            ? Number(navSearchParams.lat)
                            : null,
                    lng:
                        navSearchParams.lng !== undefined && navSearchParams.lng !== null
                            ? Number(navSearchParams.lng)
                            : null,
                })
            }
            if (navSearchParams.radius !== undefined)
                setRadius(navSearchParams.radius || null)
            if (navSearchParams.insuranceId || navSearchParams.insurance) {
                setInsurance(
                    navSearchParams.insuranceId || null,
                    navSearchParams.insurance || null
                )
            }
            if (navSearchParams.specialtyId || navSearchParams.specialty) {
                setSpecialty(
                    navSearchParams.specialtyId || null,
                    navSearchParams.specialty || null
                )
            }

            // Reset to first page for new search
            setPage(0)
            const newParams = new URLSearchParams(searchParams)
            newParams.set('page', '1')
            if (!newParams.get('limit')) {
                newParams.set('limit', '10')
            }
            setSearchParams(newParams, { replace: true })

            // Trigger API call even if all params are null (no filters)
            fetchTherapists({
                ...navSearchParams,
                lat: navSearchParams.lat ?? null,
                lng: navSearchParams.lng ?? null,
                page: 1,
                limit: 10,
            })
        } else if (
            initialSearchParams !== undefined &&
            Object.keys(initialSearchParams).length > 0
        ) {
            // Initial search params from props
            // console.log('Using initialSearchParams:', initialSearchParams)
            hasFetchedRef.current = true
            fetchTherapists(initialSearchParams)
        } else if (hasSearched || navHasSearched) {
            // If hasSearched is true but no params, use store values
            const locationData = getLocationData()
            const searchParams: SearchParams = {
                insuranceId: insuranceId || undefined,
                insurance: insuranceName || undefined,
                specialtyId: specialtyId || undefined,
                specialty: specialtyName || undefined,
                radius: radius || undefined,
                location: locationData.address || undefined,
                lat: locationData.lat ?? undefined,
                lng: locationData.lng ?? undefined,
            }
            console.log(
                'Using store values for initial fetch (hasSearched=true):',
                searchParams
            )
            hasFetchedRef.current = true
            fetchTherapists({
                ...searchParams,
                page: 1,
                limit: 10,
            })
        } else {
            // Default fetch if no search params and not already fetched
            hasFetchedRef.current = true
            fetchTherapists({ page: 1, limit: 10 })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Initial fetch on mount

    // ✅ Effect: Watch for navigation state changes (when search button clicked while already on page)
    useEffect(() => {
        // Skip if this is the initial mount (handled by the first useEffect)
        if (!hasFetchedRef.current) return

        // If navSearchParams exist (even if all values are null), trigger new search
        if (navSearchParams !== undefined) {
            console.log(
                'Navigation state changed, triggering new search:',
                navSearchParams
            )

            // Update store with search params
            if (navSearchParams.location !== undefined) {
                setLocation({
                    address: navSearchParams.location || '',
                    lat:
                        navSearchParams.lat !== undefined && navSearchParams.lat !== null
                            ? Number(navSearchParams.lat)
                            : null,
                    lng:
                        navSearchParams.lng !== undefined && navSearchParams.lng !== null
                            ? Number(navSearchParams.lng)
                            : null,
                })
            }
            if (navSearchParams.radius !== undefined)
                setRadius(navSearchParams.radius || null)
            if (navSearchParams.insuranceId || navSearchParams.insurance) {
                setInsurance(
                    navSearchParams.insuranceId || null,
                    navSearchParams.insurance || null
                )
            }
            if (navSearchParams.specialtyId || navSearchParams.specialty) {
                setSpecialty(
                    navSearchParams.specialtyId || null,
                    navSearchParams.specialty || null
                )
            }

            // Reset to first page when navigation state changes (new search)
            setPage(0)
            const newParams = new URLSearchParams(searchParams)
            newParams.set('page', '1')
            setSearchParams(newParams, { replace: true })

            // Trigger API call directly (like Search Again button)
            fetchTherapists({
                ...navSearchParams,
                lat: navSearchParams.lat ?? null,
                lng: navSearchParams.lng ?? null,
                page: 1,
                limit: rowsPerPage,
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navSearchParams, routerLocation.state]) // Watch for navigation state changes

    // Sync pagination state from URL (e.g. browser back/forward navigation).
    // API calls on user-initiated pagination are handled directly in the
    // handlers above to avoid the stale-state race condition.
    useEffect(() => {
        if (!hasFetchedRef.current) return

        const pageParam = searchParams.get('page')
        const limitParam = searchParams.get('limit')
        const newPage = pageParam ? Math.max(0, parseInt(pageParam, 10) - 1) : 0
        const newRowsPerPage = limitParam ? parseInt(limitParam, 10) : 10

        // Keep local state in sync with URL (covers browser history navigation)
        setPage(newPage)
        setRowsPerPage(newRowsPerPage)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routerLocation.search])

    // ✅ REMOVED: Automatic API call on filter changes
    // API calls are now only triggered when the search button is clicked
    // This prevents unnecessary API calls as users type or change filters

    // Initialize Google Map
    const initializeMap = () => {
        if (!mapRef.current || !window.google) return

        const map = new google.maps.Map(mapRef.current, {
            center: mapCenter,
            zoom: 10,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
            zoomControl: true,
            scaleControl: true,
            rotateControl: true,
            styles: [
                {
                    featureType: 'poi',
                    elementType: 'labels',
                    stylers: [{ visibility: 'off' }],
                },
            ],
        })

        googleMapRef.current = map
        setMapLoaded(true)

        // Add markers after map is initialized
        addMarkersToMap()
    }

    // Add markers to the map
    const addMarkersToMap = () => {
        if (!googleMapRef.current || !therapists.length) return

        // Clear existing markers
        clearMarkers()

        const bounds = new google.maps.LatLngBounds()
        let hasValidLocation = false

        // Create default and hover icons
        const defaultIcon = {
            url:
                'data:image/svg+xml;base64,' +
                btoa(`
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="18" cy="18" r="17" fill="#26a65b" stroke="white" stroke-width="2"/>
            <circle cx="18" cy="18" r="7" fill="white"/>
            <circle cx="18" cy="18" r="3" fill="#26a65b"/>
          </svg>
        `),
            scaledSize: new google.maps.Size(36, 36),
            anchor: new google.maps.Point(18, 18),
        }

        const hoverIcon = {
            url:
                'data:image/svg+xml;base64,' +
                btoa(`
          <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="22" cy="22" r="21" fill="#1e8a4c" stroke="white" stroke-width="3"/>
            <circle cx="22" cy="22" r="9" fill="white"/>
            <circle cx="22" cy="22" r="4" fill="#1e8a4c"/>
            <circle cx="22" cy="22" r="21" fill="none" stroke="#26a65b" stroke-width="2" opacity="0.3"/>
          </svg>
        `),
            scaledSize: new google.maps.Size(44, 44),
            anchor: new google.maps.Point(22, 22),
        }

        therapists.forEach(therapist => {
            if (therapist?.latitude && therapist?.longitude) {
                const position = new google.maps.LatLng(
                    therapist?.latitude,
                    therapist?.longitude
                )

                const marker = new google.maps.Marker({
                    position: position,
                    map: googleMapRef.current,
                    title: therapist?.name,
                    icon: defaultIcon,
                    animation: google.maps.Animation.DROP,
                })

                // Create info window for this marker
                const infoWindow = new google.maps.InfoWindow({
                    content: createInfoWindowContent(therapist),
                    maxWidth: 320,
                })

                // Store info window reference
                infoWindowsRef.current.push(infoWindow)

                // Add hover listeners for visual feedback
                marker.addListener('mouseover', () => {
                    marker.setIcon(hoverIcon)
                    marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1)
                    setHoveredMarkerId(therapist.id)
                })

                marker.addListener('mouseout', () => {
                    marker.setIcon(defaultIcon)
                    marker.setZIndex(google.maps.Marker.MAX_ZINDEX)
                    setHoveredMarkerId(null)
                })

                // Add click listener to marker - show info window
                marker.addListener('click', () => {
                    // Close all other info windows
                    infoWindowsRef.current.forEach(iw => {
                        if (iw !== infoWindow) {
                            iw.close()
                        }
                    })

                    // Open this info window
                    infoWindow.open(googleMapRef.current, marker)

                    // Also scroll to therapist card
                    handleMarkerClick(therapist)
                })

                markersRef.current.push(marker)
                bounds.extend(position)
                hasValidLocation = true
            }
        })

        // Fit map to bounds if we have valid locations
        if (hasValidLocation && markersRef.current.length > 0) {
            googleMapRef.current.fitBounds(bounds)

            // Don't zoom in too far if only one marker
            if (markersRef.current.length === 1) {
                googleMapRef.current.setZoom(12)
            }
        }

        // Set up global functions for info window buttons (always update to get fresh closures)
        ; (window as any).openTherapistProfile = (therapistId: string) => {
            const therapist = therapists.find(t => t.id === therapistId)
            if (therapist) {
                if (user?.role !== UserRole.CLIENT) {
                    navigate(`/therapists-profile-details/${therapistId}`)
                } else {
                    navigate(`/therapists-details/${therapistId}`)
                }
            }
        }
            ; (window as any).scrollToTherapist = (therapistId: string) => {
                const therapist = therapists.find(t => t.id === therapistId)
                if (therapist) {
                    handleMarkerClick(therapist)
                    // Close info window after scrolling
                    setTimeout(() => {
                        infoWindowsRef.current.forEach(iw => iw.close())
                    }, 500)
                }
            }
            ; (window as any).redirectToPatientLogin = (therapistId: string) => {
                // Navigate to patient login page
                navigate('/patient-login', {
                    state: {
                        returnTo: `/find-therapists`,
                        therapistId: therapistId,
                    },
                })
            }
    }

    // Clear all markers and info windows from map
    const clearMarkers = () => {
        // Close all info windows
        infoWindowsRef.current.forEach(infoWindow => {
            infoWindow.close()
        })
        infoWindowsRef.current = []

        // Remove all markers
        markersRef.current.forEach(marker => {
            marker.setMap(null)
        })
        markersRef.current = []
    }

    // Helper function to escape HTML to prevent XSS
    const escapeHtml = (text: string): string => {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }

    // Create info window content HTML
    const createInfoWindowContent = (therapist: Therapist): string => {
        const specialties =
            therapist.specialties?.slice(0, 3).join(', ') || 'General Therapy'
        const insurances =
            therapist.insurances?.slice(0, 2).join(', ') || 'Private Insurance'

        // Escape user-generated content
        const escapedName = escapeHtml(therapist.name || '')
        const escapedTitle = escapeHtml(therapist.title || '')
        const escapedRate = escapeHtml(therapist.rate || '')
        const escapedSpecialties = escapeHtml(specialties)
        const escapedInsurances = escapeHtml(insurances)
        const escapedLocation = therapist.location
            ? escapeHtml(therapist.location)
            : ''
        const escapedDistance = therapist.distance
            ? escapeHtml(therapist.distance)
            : ''
        const escapedImageUrl = therapist.image || ''
        const escapedTherapistId = escapeHtml(therapist.id || '')

        // Check if patient is authenticated
        const isAuthenticated = isPatientAuthenticated

        return `
      <div style="min-width: 280px; max-width: 320px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <div style="display: flex; gap: 12px; padding: 0; margin-bottom: 12px;">
          <img 
            src="${escapedImageUrl}" 
            alt="${escapedName}"
            style="width: 60px; height: 60px; border-radius: 8px; object-fit: cover; border: 2px solid #e5e7eb;"
            onerror="this.src=''"
          />
          <div style="flex: 1; min-width: 0;">
            <h3 style="margin: 0 0 4px 0; font-size: 16px; font-weight: 600; color: #111827; line-height: 1.3;">
              ${escapedName}
            </h3>
            <p style="margin: 0 0 6px 0; font-size: 12px; color: #26a65b; font-weight: 500;">
              ${escapedTitle}
            </p>
            <p style="margin: 0; font-size: 14px; font-weight: 600; color: #111827;">
              ${escapedRate}
            </p>
          </div>
        </div>
        
        <div style="border-top: 1px solid #e5e7eb; padding-top: 12px; margin-top: 12px;">
          <div style="margin-bottom: 8px;">
            <p style="margin: 0 0 4px 0; font-size: 11px; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">
              Specialties
            </p>
            <p style="margin: 0; font-size: 13px; color: #374151; line-height: 1.4;">
              ${escapedSpecialties}${therapist.specialties?.length > 3 ? '...' : ''}
            </p>
          </div>
          
          <div style="margin-bottom: 8px;">
            <p style="margin: 0 0 4px 0; font-size: 11px; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">
              Insurance
            </p>
            <p style="margin: 0; font-size: 13px; color: #374151; line-height: 1.4;">
              ${escapedInsurances}${therapist.insurances?.length > 2 ? '...' : ''}
            </p>
          </div>
          
          ${therapist.location
                ? `
            <div style="margin-bottom: 12px;">
              <p style="margin: 0 0 4px 0; font-size: 11px; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">
                Location
              </p>
              <p style="margin: 0; font-size: 13px; color: #374151; line-height: 1.4;">
                ${escapedLocation}${escapedDistance ? ` • ${escapedDistance}` : ''}
              </p>
            </div>
          `
                : ''
            }
        </div>
        
        <div style="display: flex; gap: 8px; margin-top: 12px; padding-top: 12px; border-top: 1px solid #e5e7eb;">
          <button 
            onclick="window.openTherapistProfile('${escapedTherapistId}')"
            style="flex: 1; padding: 8px 12px; background-color: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 6px; font-size: 13px; font-weight: 500; color: #374151; cursor: pointer; transition: all 0.2s;"
            onmouseover="this.style.backgroundColor='#e5e7eb'; this.style.borderColor='#d1d5db';"
            onmouseout="this.style.backgroundColor='#f3f4f6'; this.style.borderColor='#e5e7eb';"
          >
            View Profile
          </button>
        </div>
      </div>
    `
    }

    // Handle marker click
    const handleMarkerClick = (therapist: Therapist) => {
        console.log('Marker clicked:', therapist?.name)

        // Scroll to therapist card
        const element = document.getElementById(`therapist-${therapist?.id}`)
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
            // Add highlight effect
            element.classList.add('bg-green-50', 'border-green-200')
            setTimeout(() => {
                element.classList.remove('bg-green-50', 'border-green-200')
            }, 2000)
        }
    }

    // Load Google Maps script
    useEffect(() => {
        const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY

        if (!GOOGLE_API_KEY) {
            console.error('Google Maps API key is missing')
            return
        }

        // Check if Google Maps script is already loaded (by useJsApiLoader or manually)
        const existingScript = document.querySelector(
            'script[src*="maps.googleapis.com"]'
        )

        if (existingScript) {
            // Script already exists, wait for it to load if not already loaded
            if (window.google?.maps) {
                initializeMap()
            } else {
                // Wait for the existing script to load
                const checkInterval = setInterval(() => {
                    if (window.google?.maps) {
                        initializeMap()
                        clearInterval(checkInterval)
                    }
                }, 100)

                // Also listen to the script's load event if possible
                existingScript.addEventListener('load', () => {
                    if (window.google?.maps) {
                        initializeMap()
                    }
                    clearInterval(checkInterval)
                })

                return () => {
                    clearInterval(checkInterval)
                    clearMarkers()
                    delete (window as any).openTherapistProfile
                    delete (window as any).scrollToTherapist
                    delete (window as any).redirectToPatientLogin
                }
            }
        } else if (!window.google) {
            // No script exists, create one (should rarely happen if useJsApiLoader is used elsewhere)
            const script = document.createElement('script')
            script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=geometry,places`
            script.async = true
            script.defer = true
            script.onload = () => {
                initializeMap()
            }
            document.head.appendChild(script)
        } else {
            // Google Maps already loaded
            initializeMap()
        }

        return () => {
            clearMarkers()
            // Clean up global window functions
            delete (window as any).openTherapistProfile
            delete (window as any).scrollToTherapist
            delete (window as any).redirectToPatientLogin
        }
    }, [])

    // Update markers when therapists data changes
    useEffect(() => {
        if (mapLoaded && therapists.length > 0) {
            addMarkersToMap()
        }
    }, [therapists, mapLoaded])

    // Update markers when authentication state changes (to update info window content)
    useEffect(() => {
        if (mapLoaded && therapists.length > 0) {
            // Close all open info windows first
            infoWindowsRef.current.forEach(iw => iw.close())
            // Recreate markers with updated content
            addMarkersToMap()
        }
    }, [isPatientAuthenticated])

    // Update map center when location changes
    useEffect(() => {
        if (googleMapRef.current && mapCenter) {
            googleMapRef.current.setCenter(mapCenter)
        }
    }, [mapCenter])

    const handleBookSession = (therapist: Therapist) => {
        if (user?.role !== UserRole.CLIENT) {
            // Store the therapist they were trying to book
            setTherapistForBooking(therapist)
            // Open login dialog
            setLoginDialogOpen(true)
            return
        }
        // console.log('therapist in Find Therapist', therapist);

        const cleaned = formatTherapistData(therapist)
        const packageId = selectedPackages[therapist?.id]
        const pkg = therapist?.packages.find(p => p.id === packageId) || null

        setSelectedPackage(pkg)
        setSelectedTherapist(cleaned)
        setShowContractModal(true)
    }

    const handleLoginClick = () => {
        const therapistId = therapistForBooking?.id
        const returnPath = therapistId
            ? `/find-therapists?therapistId=${therapistId}`
            : `${routerLocation.pathname}${routerLocation.search}`

        navigate('/patient-login', {
            state: {
                returnTo: returnPath,
                therapistId: therapistId,
            },
        })
    }

    const getTitleFromDegrees = (degrees: string[] | undefined): string => {
        if (!degrees || degrees.length === 0) return 'Therapist'

        // Check for specific degrees or certifications
        // const hasDoctorate = degrees.some(
        //     deg =>
        //         deg.toString().toLowerCase().includes('phd') ||
        //         deg.toString().toLowerCase().includes('doctor')
        // )

        return degrees ? degrees.map((deg: any) => deg.name).join(', ') : 'Licensed Therapist'
    }

    const generateFallbackPackages = (): Package[] => [
        {
            id: 'fallback-1',
            name: 'Initial Consultation',
            rate: '$150',
            duration: '60 mins',
            description: 'Comprehensive initial assessment and consultation.',
            type: 'In-Person',
        },
        {
            id: 'fallback-2',
            name: 'Therapy Session',
            rate: '$120',
            duration: '45 mins',
            description: 'Standard therapy session.',
            type: 'In-Person',
        },
    ]

    const handlePackageSelect = (therapistId: string, packageId: string) => {
        setSelectedPackages(prev => ({
            ...prev,
            [therapistId]: prev[therapistId] === packageId ? null : packageId,
        }))
    }

    const handleViewSelectedPackage = (therapist: Therapist) => {
        const selectedPackageId = selectedPackages[therapist.id]
        if (!selectedPackageId || !therapist.services) return

        const selectedService = therapist.services.find(
            (service: Service) => service._id === selectedPackageId
        )

        if (selectedService) {
            setSelectedServiceForDetails(selectedService)
            setPackageDetailsDialogOpen(true)
        }
    }

    const handleClosePackageDetailsDialog = () => {
        setPackageDetailsDialogOpen(false)
        setSelectedServiceForDetails(null)
    }

    return (
        <>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <div className="flex flex-col min-h-screen bg-gray-50">
                {/* Sign-up header */}
                {user?.role !== UserRole.CLIENT && <SignupHeader />}

                {/* Summary */}
                <div className="flex flex-col w-full gap-2 px-6 py-6 mx-auto max-w-7xl">
                    {therapists.length > 0 && fromLandingPage && (
                        <Fade in timeout={800}>
                            <Link
                                to={'/'}
                                className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer mb-2"
                            >
                                <ArrowBack
                                    sx={{
                                        fontSize: '1rem',
                                        transition: 'transform 0.3s ease',
                                        '&:hover': {
                                            color: 'green',
                                            // textDecoration: 'underline',
                                            // transform: 'translateX(-4px)',
                                        },
                                    }}
                                />
                                Back to previous page
                            </Link>
                        </Fade>
                    )}
                    <h2 className="text-sm text-gray-700 md:text-base">
                        {totalTherapists > 0 ? (
                            <>
                                <span className="font-semibold text-gray-900">

                                </span>
                                {insuranceName && (
                                    <>
                                        {' '}
                                        who accept{' '}
                                        <span className="font-semibold text-gray-900">
                                            {insuranceName}
                                        </span>
                                    </>
                                )}
                                {specialtyName && (
                                    <>
                                        {' '}
                                        specializing in{' '}
                                        <span className="font-semibold text-gray-900">
                                            {specialtyName}
                                        </span>
                                    </>
                                )}
                                {location?.address && (
                                    <>
                                        {' '}
                                        in{' '}
                                        <span className="font-semibold" style={{ color: '#3BAE77', fontWeight: 700 }}>
                                            {location.address}
                                        </span>
                                    </>
                                )}
                                {radius && (
                                    <>
                                        {' '}
                                        within{' '}
                                        <span className="font-semibold" style={{ color: '#3BAE77', fontWeight: 700 }}>
                                            {radius} miles
                                        </span>
                                    </>
                                )}
                            </>
                        ) : null}
                    </h2>
                    {/* {user?.role !== UserRole.CLIENT && (
                        <button
                            onClick={() => {
                                navigate('/')
                            }}
                            className="px-5 py-2.5 bg-[#26a65b] text-white rounded-lg hover:bg-[#1e8a4c] transition-colors cursor-pointer font-medium  w-fit ml-auto"
                        >
                            Search Again
                        </button>
                    )} */}
                </div>



                {/* LIST */}
                <div className="flex flex-col w-full gap-6 px-6 pb-10 mx-auto lg:flex-row max-w-7xl">
                    <div className="flex-1 space-y-4">
                        {loading ? (
                            <div className="flex justify-center py-8">
                                <div className="text-center">
                                    <div className="w-10 h-10 mx-auto border-4 border-green-300 rounded-full border-t-green-600 animate-spin"></div>
                                    <p className="mt-4 text-gray-600">
                                        Finding the best providers...
                                    </p>
                                </div>
                            </div>
                        ) : therapists.length === 0 ? (
                            <div className="py-12 text-center">
                                <div className="max-w-md mx-auto">
                                    <HealthAndSafetyIcon
                                        style={{ fontSize: 64, color: '#e5e7eb' }}
                                    />
                                    <h3 className="mb-2 text-xl font-semibold text-gray-800">
                                        No Records Found
                                    </h3>



                                </div>
                            </div>
                        ) : (
                            therapists.map(t => (
                                <div
                                    key={t.id}
                                    id={`therapist-${t.id}`}
                                    className="flex flex-col overflow-hidden transition bg-white shadow-md lg:flex-row rounded-2xl hover:shadow-lg"
                                >
                                    {/* Image */}
                                    <div className="w-full lg:w-42">
                                        {t?.image ? (
                                            <img
                                                src={t?.image}
                                                alt={t?.firstName}
                                                className="object-cover w-full h-56 lg:h-full"
                                            />
                                        ) : (
                                            <Avatar
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    borderRadius: '10px 0 0 10px',
                                                    bgcolor: 'primary.main',
                                                }}
                                            >
                                                <Person sx={{ fontSize: 120 }} />
                                            </Avatar>
                                        )}
                                    </div>

                                    {/* Info */}
                                    <div className="flex flex-col justify-between flex-1 p-4">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                {t.name}
                                            </h3>
                                            <p className="mt-1 text-sm text-gray-700">
                                                {t.specialties?.join(', ')}
                                            </p>
                                            {t.location && (
                                                <p className="mt-1 text-sm text-gray-600">
                                                    <RoomIcon
                                                        style={{ height: '18px' }}
                                                        className="text-[#26A65B] mb-0.5"
                                                    />
                                                    {t.location} {t.distance && `• ${t.distance}`}
                                                </p>
                                            )}
                                            {/* distance */}

                                            <p className="mt-1 text-sm text-gray-600">
                                                <span className="font-semibold">Distance:</span>{' '}
                                                {t.miles}
                                            </p>
                                            <p className="mt-1 text-sm text-gray-500">
                                                <HealthAndSafetyIcon
                                                    style={{ height: '18px' }}
                                                    className="text-[#26A65B] mb-0.5"
                                                />{' '}
                                                {t.insurances?.join(', ')}
                                            </p>

                                            <p className="text-sm text-[#26a65b] bg-green-50 inline-block px-2 py-1 rounded mt-1">
                                                {t.title}, {t.experience}
                                            </p>




                                        </div>
                                        <div className="flex items-center justify-between mt-3">
                                            <p className="font-semibold text-gray-900">{t?.rate}</p>
                                            <button
                                                onClick={() => {
                                                    user?.role !== UserRole.CLIENT
                                                        ? navigate(`/therapists-profile-details/${t?.id}`)
                                                        : navigate(`/therapists-details/${t?.id}`)
                                                }}
                                                className="bg-[#3BAE77] text-white text-sm px-3 py-1.5 rounded-lg hover:bg-[#26a65b] transition-colors cursor-pointer"
                                            >
                                                View Profile
                                            </button>
                                        </div>
                                    </div>

                                    {/* Packages */}
                                    <div className="w-full lg:w-[340px] border-t lg:border-t-0 lg:border-l border-gray-100 p-4 flex flex-col justify-between">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-1.5">
                                                <h4 className="text-sm font-semibold text-gray-700">
                                                    Select a Package
                                                </h4>
                                                {/* {!selectedPackages[t.id] && (
                          <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-orange-500 rounded-full animate-pulse">
                            !
                          </span>
                        )} */}
                                            </div>
                                            {selectedPackages[t.id] && (
                                                <button
                                                    onClick={() => handleViewSelectedPackage(t)}
                                                    className="text-xs text-[#26a65b] font-semibold hover:underline cursor-pointer"
                                                >
                                                    View Selected Package
                                                </button>
                                            )}
                                        </div>

                                        <div className="flex gap-3 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                                            {t.packages.map(p => {
                                                const isSelected = selectedPackages[t.id] === p.id
                                                return (
                                                    <div
                                                        key={p?.id}
                                                        onClick={() => handlePackageSelect(t.id, p.id)}
                                                        className={`cursor-pointer border min-w-[260px] py-3 px-4 rounded-lg text-sm flex justify-between items-center transition-all ${isSelected
                                                            ? 'bg-green-50 border-[#26a65b] shadow-sm'
                                                            : 'bg-white border-gray-200 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        <div className="w-full">
                                                            <p className="font-semibold text-gray-800">
                                                                {p?.rate}{' '}
                                                                <span className="text-xs text-gray-500">
                                                                    {p?.duration}
                                                                </span>
                                                            </p>
                                                            <p className="mt-1 text-xs text-gray-600">
                                                                {p?.name}
                                                            </p>
                                                            <p className="mt-1 text-xs text-gray-400">
                                                                {p?.type}
                                                            </p>
                                                        </div>
                                                        {isSelected && (
                                                            <span className="text-[#26a65b] text-lg ml-2">
                                                                ✔
                                                            </span>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>

                                        <div className="mt-4">
                                            <button
                                                disabled={!selectedPackages[t.id]}
                                                className={`w-full py-2.5 rounded-lg text-sm font-medium transition-all ${selectedPackages[t.id]
                                                    ? 'bg-[#26a65b] text-white hover:bg-[#1e8a4c] shadow-sm cursor-pointer transform hover:scale-[1.02]'
                                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                    }`}
                                                onClick={() => handleBookSession(t)}
                                                title={
                                                    !selectedPackages[t.id]
                                                        ? 'Please select a package first'
                                                        : 'Book your session'
                                                }
                                            >
                                                {selectedPackages[t.id] ? (
                                                    'Book Session'
                                                ) : (
                                                    <span className="flex items-center justify-center gap-1.5">
                                                        <span>Select a Package to Book</span>
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}

                        {/* Pagination */}
                        {!loading && therapists.length > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    mt: 4,
                                    pt: 3,
                                    borderTop: '1px solid',
                                    borderColor: 'grey.200',
                                }}
                            >
                                <AdvancedPagination
                                    count={totalTherapists}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                />
                            </Box>
                        )}
                    </div>

                    {/* Interactive Map */}
                    <div className="w-full lg:w-[30%] bg-white shadow-md rounded-2xl overflow-hidden h-[400px] lg:h-[600px] sticky top-6">
                        <div className="p-4 bg-[#3bae78] text-white">
                            <h3 className="font-semibold">Provider Locations</h3>
                            <p className="text-sm text-green-100">
                                {therapists.filter(t => t.latitude && t.longitude).length}{' '}
                                providers with location data
                            </p>
                        </div>
                        <div
                            ref={mapRef}
                            className="w-full h-[calc(100%-80px)]"
                            style={{ minHeight: '300px' }}
                        />
                        <div className="p-2 text-xs text-center text-gray-600 border-t border-gray-200 bg-gray-50">
                            <span className="font-medium">Hover</span> over markers to
                            highlight • <span className="font-medium">Click</span> to view
                            provider details
                        </div>
                    </div>
                </div>
            </div>

            {showContractModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999] overflow-hidden">
                    <TherapistBooking
                        therapistId={selectedTherapist?.id}
                        therapistData={selectedTherapist}
                        selectedPackage={selectedPackage}
                        defaultDate={null}
                        onClose={() => setShowContractModal(false)}
                        onComplete={booking => {
                            console.log('Booking Completed:', booking)
                            setShowContractModal(false)
                        }}
                    />
                </div>
            )}

            {/* Service Package Details Dialog */}
            <Dialog
                open={packageDetailsDialogOpen}
                onClose={handleClosePackageDetailsDialog}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 3,
                        maxHeight: '90vh',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pb: 2,
                        background: `linear-gradient(135deg, ${alpha(
                            theme.palette.primary.main,
                            0.08
                        )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                color: 'primary.main',
                            }}
                        >
                            <InfoIcon />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Service Package Details
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Complete information about this service
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={handleClosePackageDetailsDialog}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                color: 'error.main',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    {selectedServiceForDetails ? (
                        <Box sx={{ p: 3 }}>
                            <Stack spacing={3}>
                                {/* Header Section */}
                                <Box
                                    sx={{
                                        p: 3,
                                        borderRadius: 2,
                                        background: `linear-gradient(135deg, ${alpha(
                                            theme.palette.primary.main,
                                            0.1
                                        )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    }}
                                >
                                    <Stack spacing={2}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                flexWrap: 'wrap',
                                                gap: 2,
                                            }}
                                        >
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h5"
                                                    sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}
                                                >
                                                    {selectedServiceForDetails.name}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ lineHeight: 1.6 }}
                                                >
                                                    {selectedServiceForDetails.short_description ||
                                                        'No description available'}
                                                </Typography>
                                            </Box>
                                            {/* {selectedServiceForDetails.status && (
                        <Chip
                          label={selectedServiceForDetails.status.toUpperCase()}
                          size="small"
                          color={
                            selectedServiceForDetails.status === 'published'
                              ? 'success'
                              : 'default'
                          }
                          sx={{ fontWeight: 600 }}
                        />
                      )} */}
                                        </Box>
                                    </Stack>
                                </Box>

                                {/* Long Description */}
                                {selectedServiceForDetails.long_description && (
                                    <Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1.5,
                                            }}
                                        >
                                            <DescriptionIcon
                                                sx={{ fontSize: 20, color: 'primary.main' }}
                                            />
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontWeight: 600, color: 'text.primary' }}
                                            >
                                                Detailed Description
                                            </Typography>
                                        </Box>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2.5,
                                                bgcolor: alpha(theme.palette.background.paper, 0.6),
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}
                                            >
                                                {selectedServiceForDetails.long_description}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                )}

                                {/* Basic Information Grid */}
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
                                    >
                                        Basic Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {/* Age Range */}
                                        {/* {(selectedServiceForDetails.min_age ||
                      selectedServiceForDetails.max_age) && (
                      <Grid item xs={12} sm={6}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            height: '100%',
                            bgcolor: alpha(theme.palette.info.main, 0.04),
                            borderColor: alpha(theme.palette.info.main, 0.2),
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Age Range
                            </Typography>
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: 'info.main' }}
                          >
                            {selectedServiceForDetails.min_age || 'Any'} -{' '}
                            {selectedServiceForDetails.max_age || 'Any'} years
                          </Typography>
                        </Paper>
                      </Grid>
                    )} */}
                                        {selectedServiceForDetails.sessionFee && (
                                            <Grid item xs={12} sm={6}>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        p: 2,
                                                        height: '100%',
                                                        bgcolor: alpha(theme.palette.success.main, 0.04),
                                                        borderColor: alpha(theme.palette.success.main, 0.2),
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                                                    >
                                                        Session Fee
                                                    </Typography>
                                                    <Typography
                                                        variant="h5"
                                                        sx={{ fontWeight: 700, color: 'success.main' }}
                                                    >
                                                        ${selectedServiceForDetails.sessionFee}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        per session
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        )}

                                        {/* Session Length */}
                                        {selectedServiceForDetails.session_length && (
                                            <Grid item xs={12} sm={6}>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        p: 2,
                                                        height: '100%',
                                                        bgcolor: alpha(theme.palette.secondary.main, 0.04),
                                                        borderColor: alpha(
                                                            theme.palette.secondary.main,
                                                            0.2
                                                        ),
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            mb: 1,
                                                        }}
                                                    >
                                                        <ScheduleIcon
                                                            sx={{ fontSize: 18, color: 'secondary.main' }}
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ fontWeight: 600 }}
                                                        >
                                                            Session Length
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{ fontWeight: 600, color: 'secondary.main' }}
                                                    >
                                                        {selectedServiceForDetails.session_length} mins
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        )}

                                        {/* Session Buffer */}
                                        {selectedServiceForDetails.session_buffer != null && (
                                            <Grid item xs={12} sm={6}>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        p: 2,
                                                        height: '100%',
                                                        bgcolor: alpha(theme.palette.info.main, 0.04),
                                                        borderColor: alpha(theme.palette.info.main, 0.2),
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            mb: 1,
                                                        }}
                                                    >
                                                        <AccessTimeIcon
                                                            sx={{ fontSize: 18, color: 'info.main' }}
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ fontWeight: 600 }}
                                                        >
                                                            Session Buffer
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{ fontWeight: 600, color: 'info.main' }}
                                                    >
                                                        {selectedServiceForDetails.session_buffer} mins
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        )}

                                        {/* Languages */}
                                        {selectedServiceForDetails.language &&
                                            selectedServiceForDetails.language.length > 0 && (
                                                <Grid item xs={12}>
                                                    <Paper
                                                        variant="outlined"
                                                        sx={{
                                                            p: 2,
                                                            bgcolor: alpha(theme.palette.success.main, 0.04),
                                                            borderColor: alpha(
                                                                theme.palette.success.main,
                                                                0.2
                                                            ),
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                mb: 1.5,
                                                            }}
                                                        >
                                                            <LanguageIcon
                                                                sx={{ fontSize: 18, color: 'success.main' }}
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ fontWeight: 600 }}
                                                            >
                                                                Languages Offered
                                                            </Typography>
                                                        </Box>
                                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                                            {selectedServiceForDetails.language.map(
                                                                (lang, idx) => (
                                                                    <Chip
                                                                        key={idx}
                                                                        label={lang}
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: alpha(
                                                                                theme.palette.success.main,
                                                                                0.12
                                                                            ),
                                                                            color: 'success.main',
                                                                            fontWeight: 600,
                                                                            mb: 1,
                                                                        }}
                                                                    />
                                                                )
                                                            )}
                                                        </Stack>
                                                    </Paper>
                                                </Grid>
                                            )}

                                        {/* Service Radius */}
                                        {selectedServiceForDetails.service_radius && (
                                            <Grid item xs={12} sm={6}>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        p: 2,
                                                        bgcolor: alpha(theme.palette.info.main, 0.04),
                                                        borderColor: alpha(theme.palette.info.main, 0.2),
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            mb: 1,
                                                        }}
                                                    >
                                                        <RoomIcon
                                                            sx={{ fontSize: 18, color: 'info.main' }}
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ fontWeight: 600 }}
                                                        >
                                                            Service Radius
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{ fontWeight: 600, color: 'info.main' }}
                                                    >
                                                        {selectedServiceForDetails.service_radius} miles
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>

                                {/* Fees & Cancellation Policies */}
                                {(selectedServiceForDetails.travelOnsiteSurcharge != null ||
                                    selectedServiceForDetails.cancellationWindowHours != null ||
                                    selectedServiceForDetails.noShowFee != null ||
                                    selectedServiceForDetails.lateCancellationFee != null) && (
                                        <Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mb: 2,
                                                }}
                                            >
                                                <PaymentIcon
                                                    sx={{ fontSize: 20, color: 'warning.main' }}
                                                />
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{ fontWeight: 600, color: 'text.primary' }}
                                                >
                                                    Fees & Cancellation Policies
                                                </Typography>
                                            </Box>

                                            <Grid container spacing={2}>
                                                {selectedServiceForDetails.travelOnsiteSurcharge !=
                                                    null && (
                                                        <Grid item xs={12} sm={6} md={3}>
                                                            <Paper
                                                                variant="outlined"
                                                                sx={{
                                                                    p: 2,
                                                                    height: '100%',
                                                                    bgcolor: alpha(theme.palette.info.main, 0.04),
                                                                    borderColor: alpha(theme.palette.info.main, 0.2),
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                                                                >
                                                                    Travel/Onsite Surcharge
                                                                </Typography>
                                                                <Typography
                                                                    variant="h6"
                                                                    sx={{ fontWeight: 700, color: 'info.main' }}
                                                                >
                                                                    $
                                                                    {selectedServiceForDetails.travelOnsiteSurcharge.toFixed(
                                                                        2
                                                                    )}
                                                                </Typography>
                                                            </Paper>
                                                        </Grid>
                                                    )}

                                                {selectedServiceForDetails.cancellationWindowHours !=
                                                    null && (
                                                        <Grid item xs={12} sm={6} md={3}>
                                                            <Paper
                                                                variant="outlined"
                                                                sx={{
                                                                    p: 2,
                                                                    height: '100%',
                                                                    bgcolor: alpha(
                                                                        theme.palette.secondary.main,
                                                                        0.04
                                                                    ),
                                                                    borderColor: alpha(
                                                                        theme.palette.secondary.main,
                                                                        0.2
                                                                    ),
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                        mb: 1,
                                                                    }}
                                                                >
                                                                    <ScheduleIcon
                                                                        sx={{ fontSize: 18, color: 'secondary.main' }}
                                                                    />
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{ fontWeight: 600 }}
                                                                    >
                                                                        Cancellation Window
                                                                    </Typography>
                                                                </Box>
                                                                <Typography
                                                                    variant="h6"
                                                                    sx={{ fontWeight: 700, color: 'secondary.main' }}
                                                                >
                                                                    {
                                                                        selectedServiceForDetails.cancellationWindowHours
                                                                    }{' '}
                                                                    hrs
                                                                </Typography>
                                                            </Paper>
                                                        </Grid>
                                                    )}

                                                {selectedServiceForDetails.noShowFee != null && (
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Paper
                                                            variant="outlined"
                                                            sx={{
                                                                p: 2,
                                                                height: '100%',
                                                                bgcolor: alpha(theme.palette.error.main, 0.04),
                                                                borderColor: alpha(theme.palette.error.main, 0.2),
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                                                            >
                                                                No-Show Fee
                                                            </Typography>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{ fontWeight: 700, color: 'error.main' }}
                                                            >
                                                                ${selectedServiceForDetails.noShowFee.toFixed(2)}
                                                            </Typography>
                                                        </Paper>
                                                    </Grid>
                                                )}

                                                {selectedServiceForDetails.lateCancellationFee !=
                                                    null && (
                                                        <Grid item xs={12} sm={6} md={3}>
                                                            <Paper
                                                                variant="outlined"
                                                                sx={{
                                                                    p: 2,
                                                                    height: '100%',
                                                                    bgcolor: alpha(theme.palette.warning.main, 0.04),
                                                                    borderColor: alpha(
                                                                        theme.palette.warning.main,
                                                                        0.2
                                                                    ),
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                                                                >
                                                                    Late Cancellation Fee
                                                                </Typography>
                                                                <Typography
                                                                    variant="h6"
                                                                    sx={{ fontWeight: 700, color: 'warning.main' }}
                                                                >
                                                                    $
                                                                    {selectedServiceForDetails.lateCancellationFee.toFixed(
                                                                        2
                                                                    )}
                                                                </Typography>
                                                            </Paper>
                                                        </Grid>
                                                    )}
                                            </Grid>
                                        </Box>
                                    )}

                                {/* Tags */}
                                {selectedServiceForDetails.tags &&
                                    selectedServiceForDetails.tags.length > 0 && (
                                        <Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mb: 1.5,
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    Tags
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                {selectedServiceForDetails.tags.map((tag, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        label={tag}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: alpha(theme.palette.info.main, 0.12),
                                                            color: 'info.main',
                                                            fontWeight: 600,
                                                            mb: 1,
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}

                                {/* Specialization */}
                                {selectedServiceForDetails.specialization && (
                                    <Box>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}
                                        >
                                            Specialization
                                        </Typography>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                                                borderColor: alpha(theme.palette.primary.main, 0.2),
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{ fontWeight: 600, color: 'primary.main' }}
                                            >
                                                {selectedServiceForDetails.specialization.name}
                                            </Typography>
                                            {selectedServiceForDetails.specialization.description && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ mt: 0.5, display: 'block' }}
                                                >
                                                    {selectedServiceForDetails.specialization.description}
                                                </Typography>
                                            )}
                                        </Paper>
                                    </Box>
                                )}

                                {/* Insurance Names */}
                                {selectedServiceForDetails.insurance &&
                                    selectedServiceForDetails.insurance.length > 0 && (
                                        <Box>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}
                                            >
                                                Insurance Providers
                                            </Typography>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                                                    borderColor: alpha(theme.palette.primary.main, 0.2),
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        mb: 1.5,
                                                    }}
                                                >
                                                    <HealthAndSafetyIcon
                                                        sx={{ fontSize: 20, color: 'primary.main' }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        Accepted Insurance
                                                    </Typography>
                                                </Box>
                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                    {selectedServiceForDetails.insurance.map(
                                                        (ins, idx) => (
                                                            <Chip
                                                                key={ins._id || idx}
                                                                label={ins.payer_name}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: alpha(
                                                                        theme.palette.primary.main,
                                                                        0.12
                                                                    ),
                                                                    color: 'primary.main',
                                                                    fontWeight: 500,
                                                                }}
                                                            />
                                                        )
                                                    )}
                                                </Stack>
                                            </Paper>
                                        </Box>
                                    )}
                            </Stack>
                        </Box>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                Package details are not available.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.6),
                    }}
                >
                    <Button
                        onClick={handleClosePackageDetailsDialog}
                        variant="contained"
                        sx={{ fontWeight: 600 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Login Required Dialog */}
            <Dialog
                open={loginDialogOpen}
                onClose={() => {
                    setLoginDialogOpen(false)
                    setTherapistForBooking(null)
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 3,
                        position: 'relative',
                        px: 3,
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 500, color: 'grey.700' }}>
                        Login Required to Book Session
                    </Typography>
                    <IconButton
                        onClick={() => {
                            setLoginDialogOpen(false)
                            setTherapistForBooking(null)
                        }}
                        sx={{
                            position: 'absolute',
                            right: 24,
                            top: 24,
                            color: '#26a65b',
                            '&:hover': {
                                bgcolor: 'rgba(38, 166, 91, 0.08)',
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ px: 3, py: 2 }}>
                    {therapistForBooking && (
                        <Box
                            sx={{
                                mb: 3,
                                p: 2.5,
                                borderRadius: 2,
                                bgcolor: 'rgba(38, 166, 91, 0.04)',
                                border: '1px solid rgba(38, 166, 91, 0.15)',
                            }}
                        >
                            <Typography
                                variant="overline"
                                sx={{ color: 'grey.500', letterSpacing: 1, fontWeight: 600 }}
                            >
                                Provider Profile
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 700, color: 'grey.900', mb: 1 }}
                            >
                                {therapistForBooking.name}
                            </Typography>
                            <Stack spacing={1} sx={{ mb: 1.5 }}>
                                {therapistForBooking.location && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <RoomIcon sx={{ fontSize: '1rem', color: '#26a65b' }} />
                                        <Typography variant="body2" sx={{ color: 'grey.700' }}>
                                            {therapistForBooking.location}
                                            {therapistForBooking.distance &&
                                                ` • ${therapistForBooking.distance}`}
                                        </Typography>
                                    </Box>
                                )}
                                {therapistForBooking.specialties &&
                                    therapistForBooking.specialties.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <HealthAndSafetyIcon
                                                sx={{ fontSize: '1rem', color: '#26a65b' }}
                                            />
                                            <Typography variant="body2" sx={{ color: 'grey.700' }}>
                                                {therapistForBooking.specialties.slice(0, 3).join(', ')}
                                                {therapistForBooking.specialties.length > 3 && '...'}
                                            </Typography>
                                        </Box>
                                    )}
                                {therapistForBooking.rate && (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'grey.600', fontWeight: 600 }}
                                    >
                                        {therapistForBooking.rate}
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    )}

                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, color: 'grey.800', mb: 1 }}
                        >
                            Login Required to Book Session
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'grey.600', lineHeight: 1.6, mb: 3 }}
                        >
                            Please log in to your patient account or create a new account to
                            book a session with this provider and access all booking features.
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Person />}
                            onClick={handleLoginClick}
                            sx={{
                                bgcolor: '#26a65b',
                                color: 'white',
                                borderRadius: 2,
                                px: { sm: 2, md: 3 },
                                py: 1,
                                fontSize: { sm: '0.75rem', md: '0.875rem' },
                                fontWeight: 600,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    bgcolor: '#1e8a4c',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 25px rgba(38, 166, 91, 0.3)',
                                },
                            }}
                        >
                            Sign Up / Login
                        </Button>
                    </Paper>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => {
                            setLoginDialogOpen(false)
                            setTherapistForBooking(null)
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ListComponent
